import { Suspense, lazy, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MDEditor from '@uiw/react-md-editor';

import '@uiw/react-md-editor/markdown-editor.css';
import '@uiw/react-markdown-preview/markdown.css';

import { EDITOR_EXTRA_COMMANDS, buildEditorCommands } from './MDeditorMediaCommands';
import ArticleSidebar from './ArticleSidebar';
import ArticleEditorSidebar from './ArticleEditorSidebar';
import ArticleLinkField from './ArticleLinkField';
import ArticleBody from './ArticleBody';
import ReimportDialog from './ReimportDialog';
import Button from './ui/Button';
import PluginEditor from '../plugins/PluginEditor';
import { mediaFile } from '../plugins/pluginFrame';
import { pluginArticle, pluginBlockText } from '../plugins/pluginBlocks';

// Loaded only when an author opens the editor - readers never need it.
const VisualEditor = lazy(() => import('./VisualEditor'));

export const ARTICLE_TITLE_ID = 'article-title';

const REIMPORT_ID = 'article-reimport';

// The preview beside the Markdown shows the text as the article will: steps as a carousel, tabs, comparisons.
const NO_HEADINGS = [];
const MD_COMPONENTS = {
  preview: (source) => <ArticleBody articleText={source} headings={NO_HEADINGS} />,
};

// Which view of the text editor an author last chose, per browser.
const EDITOR_MODE_KEY = 'disi.articleEditorMode';
const EDITOR_MODES = ['visual', 'code'];

function readEditorMode() {
  try {
    const stored = window.localStorage.getItem(EDITOR_MODE_KEY);
    return EDITOR_MODES.includes(stored) ? stored : 'visual';
  } catch {
    return 'visual';
  }
}

function storeEditorMode(mode) {
  try {
    window.localStorage.setItem(EDITOR_MODE_KEY, mode);
  } catch {
    // Without storage the choice just lasts until the page is left.
  }
}

const EDITOR_INPUTS = {
  image: 'article-image-input',
  video: 'article-video-input',
  audio: 'article-audio-input',
};

const EDITOR_COMMANDS = buildEditorCommands(EDITOR_INPUTS);

const EDITOR_COMMANDS_WITH_REIMPORT = buildEditorCommands({
  ...EDITOR_INPUTS,
  reimport: REIMPORT_ID,
});

const MODE_ICONS = { visual: 'fa-eye', code: 'fa-code' };

/** One button that switches the text to the other view and names that view. */
function EditorModeSwitch({ mode, onChange, disabled }) {
  const { t } = useTranslation();
  const other = mode === 'visual' ? 'code' : 'visual';

  return (
    <button
      type="button"
      className="editor-mode-switch"
      title={t(`visualEditor.switchTo.${other}`)}
      aria-label={t(`visualEditor.switchTo.${other}`)}
      disabled={disabled}
      onClick={() => onChange(other)}
    >
      <i className={`fa-solid ${MODE_ICONS[other]}`} aria-hidden="true" />
      <span>{t(`visualEditor.mode.${other}`)}</span>
    </button>
  );
}

/** The burger in the title row of a plugin editor: shows or hides the side boxes. */
function SidebarToggle({ open, onToggle }) {
  const { t } = useTranslation();
  const label = t(open ? 'articleEditor.sidebarHide' : 'articleEditor.sidebarShow');

  return (
    <button
      type="button"
      className={`editor-mode-switch article-sidebar-toggle${open ? ' is-open' : ''}`}
      title={label}
      aria-label={label}
      aria-expanded={open}
      onClick={onToggle}
    >
      <i className="fa-solid fa-bars" aria-hidden="true" />
    </button>
  );
}

/**
 * The article editor: title, link or text, and the boxes beside it. Creating
 * an article (pages/CreateArticle) and editing one (components/MainWindow) used
 * to be two copies of this screen that had begun to differ; now they differ
 * only in what the draft starts with (useArticleDraft) and what saving does.
 *
 * `linkMode`: a link item - an address instead of a text, so no text editor.
 * `plugin`: a plugin's block - its editor page instead of the text editor;
 * the text is the one block it keeps up to date, its files stored as article
 * media (`pluginScope`: the department the editor works for, or null).
 * `sidebar`: what only one of the two screens shows in the side boxes - the
 * kind picker while creating, the article being edited otherwise.
 */
function ArticleEditorForm({
  draft,
  linkMode = false,
  plugin = null,
  pluginScope = null,
  showLinkHint = false,
  canReimport = false,
  preview = 'edit',
  saving = false,
  onSubmit,
  onCancel,
  labels,
  uploadTarget = 'article',
  departments = [],
  departmentId = null,
  onDepartmentChange,
  sidebar = {},
}) {
  const [reimportOpen, setReimportOpen] = useState(false);
  const [editorMode, setEditorMode] = useState(readEditorMode);
  const visualEditorRef = useRef(null);
  const { values, set, titleError, error, ai } = draft;

  // The plugin block the editor opens with; read once (PluginEditor).
  // A plugin editor needs the whole width: its side boxes start hidden.
  const [sidebarOpen, setSidebarOpen] = useState(!plugin);
  const [initialPluginData] = useState(() => (plugin ? pluginArticle(values.text, [plugin])?.data ?? null : null));

  // The block's stored files by field: an address once saved, a staged blob
  // until then. The editor hands a new file over as base64.
  const pluginMediaRef = useRef(
    Object.fromEntries(
      (plugin?.block?.media ?? [])
        .filter((field) => typeof initialPluginData?.[field] === 'string' && initialPluginData[field])
        .map((field) => [field, initialPluginData[field]]),
    ),
  );

  function pluginChanged(data, media) {
    for (const [field, file] of Object.entries(media ?? {})) {
      if (plugin.block.media?.includes(field) && file?.base64) {
        pluginMediaRef.current[field] = draft.stage(mediaFile(file, field), 'image');
      }
    }

    const stored = data && typeof data === 'object' && !Array.isArray(data)
      ? { ...data, ...pluginMediaRef.current }
      : data;
    set('text', pluginBlockText(plugin, stored));

    const title = data?.title ?? data?.titel;
    if (!values.title.trim() && typeof title === 'string' && title.trim()) set('title', title.trim());
  }

  function trackCursor(event) {
    draft.cursorPositionRef.current = event.target.selectionStart;
  }

  function changeEditorMode(mode) {
    setEditorMode(mode);
    storeEditorMode(mode);
  }

  function insertMedia(event, mediaType) {
    if (saving) {
      event.target.value = '';
      return;
    }

    const visual = editorMode === 'visual' && visualEditorRef.current;

    draft.insertMedia(
      event,
      mediaType,
      visual ? (markdown) => visualEditorRef.current.insertMarkdown(markdown) : undefined
    );
  }

  return (
    <>
      <div className="maincol markdown-content article-editor">
        <div className={`account-field-row${linkMode ? '' : ' article-editor-title-row'}`}>
          <label htmlFor={ARTICLE_TITLE_ID}>
            {labels.title}
            <span className="field-required" aria-hidden="true"> *</span>
          </label>

          <input
            id={ARTICLE_TITLE_ID}
            type="text"
            value={values.title}
            onChange={(event) => set('title', event.target.value)}
            required
            aria-invalid={Boolean(titleError)}
            aria-describedby={titleError ? 'article-title-error' : undefined}
            disabled={saving}
          />

          {!linkMode && !plugin && (
            <EditorModeSwitch mode={editorMode} onChange={changeEditorMode} disabled={saving} />
          )}

          {plugin && <SidebarToggle open={sidebarOpen} onToggle={() => setSidebarOpen((open) => !open)} />}

          {titleError && (
            <p id="article-title-error" className="field-error" role="alert">
              <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
              <span>{titleError}</span>
            </p>
          )}
        </div>

        {linkMode && (
          <>
            <ArticleLinkField
              id="article-external-url"
              value={values.externalUrl}
              onChange={(value) => set('externalUrl', value)}
              disabled={saving}
            />

            {showLinkHint && <p className="article-link-hint">{labels.linkHint}</p>}
          </>
        )}

        {plugin && (
          <PluginEditor
            plugin={plugin}
            initial={initialPluginData}
            scope={pluginScope}
            onChange={pluginChanged}
            disabled={saving}
          />
        )}

        {!linkMode && !plugin && (
          <>
            {Object.entries(EDITOR_INPUTS).map(([mediaType, inputId]) => (
              <input
                key={mediaType}
                id={inputId}
                type="file"
                accept={`${mediaType}/*`}
                hidden
                onChange={(event) => insertMedia(event, mediaType)}
              />
            ))}

            {canReimport && (
              <button
                id={REIMPORT_ID}
                type="button"
                hidden
                onClick={() => setReimportOpen(true)}
              />
            )}

            {reimportOpen && (
              <ReimportDialog
                onClose={() => setReimportOpen(false)}
                onImported={(imported) => {
                  draft.replaceTextFromImport(imported);
                  setReimportOpen(false);
                }}
              />
            )}

            {editorMode === 'visual' ? (
              <Suspense fallback={<div className="visual-editor" aria-busy="true" />}>
                <VisualEditor
                  ref={visualEditorRef}
                  value={values.text}
                  onChange={(value) => set('text', value)}
                  mediaInputs={EDITOR_INPUTS}
                  reimportId={canReimport ? REIMPORT_ID : null}
                  disabled={saving}
                  onFile={(file, mediaType, insert) => {
                    if (!saving) draft.insertFile(file, mediaType, insert);
                  }}
                />
              </Suspense>
            ) : (
              <div
                data-color-mode={
                  document.documentElement.dataset.theme === 'dark'
                    ? 'dark'
                    : 'light'
                }
                className="article-editor-md"
              >
                <MDEditor
                  value={values.text}
                  onChange={(value) => set('text', value ?? '')}
                  onSelect={trackCursor}
                  onClick={trackCursor}
                  onKeyUp={trackCursor}
                  height="100%"
                  commands={canReimport ? EDITOR_COMMANDS_WITH_REIMPORT : EDITOR_COMMANDS}
                  extraCommands={EDITOR_EXTRA_COMMANDS}
                  preview={preview}
                  components={MD_COMPONENTS}
                  highlightEnable={false}
                />
              </div>
            )}
          </>
        )}

        {error && (
          <div className="login-message login-message-error" role="alert">
            <i className="fa-solid fa-circle-exclamation" />
            <span>{error}</span>
          </div>
        )}

        <div className="article-editor-actions">
          <Button variant="secondary" onClick={onCancel} disabled={saving}>
            {labels.cancel}
          </Button>

          <Button variant="primary" onClick={onSubmit} disabled={saving}>
            {saving ? labels.saving : labels.submit}
          </Button>
        </div>
      </div>

      {sidebarOpen && (
        <ArticleSidebar
          editorSidebar={
            <ArticleEditorSidebar
              {...sidebar}
              allCollapsed={Boolean(plugin)}
              tags={values.tags}
              onTagsChange={(tags) => set('tags', tags)}
              departments={departments}
              departmentId={departmentId}
              onDepartmentChange={onDepartmentChange}
              icon={values.icon}
              onIconChange={(icon) => set('icon', icon)}
              summary={values.summary}
              onSummaryChange={(summary) => set('summary', summary)}
              onAISummary={() => ai.handleAISummary(saving)}
              isAnalyzingWithAI={ai.isAnalyzingWithAI}
              aiError={ai.aiError}
              onAISuggestTags={() => ai.handleAISuggestTags(saving)}
              aiTagSuggestions={ai.aiTagSuggestions}
              onAcceptAITag={ai.acceptAITag}
              onRemoveAITag={ai.removeTag}
              isSuggestingTags={ai.isSuggestingTags}
              aiTagsError={ai.aiTagsError}
              uploadTarget={uploadTarget}
              disabled={saving}
            />
          }
        />
      )}
    </>
  );
}

export default ArticleEditorForm;
